import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Skeleton,
  FormModal,
  ConfirmModal,
  Input,
  TextArea,
} from '../../components/ui';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/adminApi';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import {
  ArrowBack,
  Restaurant,
  Edit,
  Delete,
  Visibility,
  Close,
  Warning,
  LocalFireDepartment,
  AccessTime,
  CalendarMonth,
  Person
} from '../../mui-icons';
import './UserMealsPage.css';

const UserMealsPage = () => {
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const { canEdit } = useAdminAuth();

  // User info
  const [user, setUser] = useState(null);

  // Meals state
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [weekDayFilter, setWeekDayFilter] = useState('');
  const [mealTypeFilter, setMealTypeFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  // Detail Modal
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [mealDetail, setMealDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ dish_name: '', dish_description: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Load meals
  const loadMeals = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(weekDayFilter && { week_day: weekDayFilter }),
        ...(mealTypeFilter && { meal_type: mealTypeFilter })
      };

      const response = await adminAPI.getUserMeals(userId, params);

      if (response.status === 'success') {
        setMeals(response.data.meals || []);
        setUser(response.data.user || null);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          pages: response.data.pagination?.pages || 0
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar refeições:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, pagination.page, pagination.limit, weekDayFilter, mealTypeFilter]);

  useEffect(() => {
    loadMeals();
  }, [loadMeals]);

  // Open meal detail
  const openDetail = async (meal) => {
    setSelectedMeal(meal);
    setDetailLoading(true);
    setMealDetail(null);

    try {
      const response = await adminAPI.getUserMeal(userId, meal.idusersmeal);
      if (response.status === 'success') {
        setMealDetail(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Close detail
  const closeDetail = () => {
    setSelectedMeal(null);
    setMealDetail(null);
  };

  // Open edit modal
  const openEditModal = (meal) => {
    setEditForm({
      dish_name: meal.dish_name || '',
      dish_description: meal.dish_description || ''
    });
    setSelectedMeal(meal);
    setShowEditModal(true);
  };

  // Handle edit
  const handleEdit = async (e) => {
    e.preventDefault();
    setEditLoading(true);

    try {
      await adminAPI.updateUserMeal(userId, selectedMeal.idusersmeal, editForm);
      setShowEditModal(false);
      loadMeals();
    } catch (err) {
      alert(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  // Open delete modal
  const openDeleteModal = (meal) => {
    setSelectedMeal(meal);
    setShowDeleteModal(true);
  };

  // Handle delete
  const handleDelete = async () => {
    setDeleteLoading(true);

    try {
      await adminAPI.deleteUserMeal(userId, selectedMeal.idusersmeal);
      setShowDeleteModal(false);
      loadMeals();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Format helpers
  const getDayLabel = (day) => {
    const days = {
      monday: 'Segunda',
      tuesday: 'Terça',
      wednesday: 'Quarta',
      thursday: 'Quinta',
      friday: 'Sexta',
      saturday: 'Sábado',
      sunday: 'Domingo'
    };
    return days[day] || day;
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return time.substring(0, 5);
  };

  return (
    <div className="user-meals-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <button className="back-button" onClick={() => navigate('/admin/users')}>
            <ArrowBack />
          </button>
          <Restaurant sx={{ fontSize: 32 }} />
          <div>
            <h1>Refeições do Usuário</h1>
            {user && (
              <p>
                <Person sx={{ fontSize: 14 }} /> {user.name} ({user.mail})
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-card pn-card pn-card--default filters-card">
        <div className="filters-row">
          <div className="filter-group">
            <label>Dia da Semana</label>
            <select
              value={weekDayFilter}
              onChange={(e) => {
                setWeekDayFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">Todos</option>
              <option value="monday">Segunda</option>
              <option value="tuesday">Terça</option>
              <option value="wednesday">Quarta</option>
              <option value="thursday">Quinta</option>
              <option value="friday">Sexta</option>
              <option value="saturday">Sábado</option>
              <option value="sunday">Domingo</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Tipo de Refeição</label>
            <select
              value={mealTypeFilter}
              onChange={(e) => {
                setMealTypeFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">Todos</option>
              <option value="Café da Manhã">Café da Manhã</option>
              <option value="Lanche da Manhã">Lanche da Manhã</option>
              <option value="Almoço">Almoço</option>
              <option value="Lanche da Tarde">Lanche da Tarde</option>
              <option value="Jantar">Jantar</option>
              <option value="Ceia">Ceia</option>
            </select>
          </div>
        </div>
      </div>

      {/* Meals List */}
      <div className="admin-card pn-card pn-card--default">
        {error ? (
          <div className="error-state">
            <Warning sx={{ fontSize: 48 }} />
            <p>{error}</p>
            <Button variant="primary" onClick={loadMeals}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <>
            <div className="table-header-info">
              <span>{pagination.total} refeição(ões) encontrada(s)</span>
            </div>

            <div className="meals-grid">
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <div key={i} className="meal-card skeleton-card">
                    <Skeleton height={150} />
                  </div>
                ))
              ) : meals.length === 0 ? (
                <div className="empty-state">
                  <Restaurant sx={{ fontSize: 48, opacity: 0.3 }} />
                  <p>Nenhuma refeição encontrada</p>
                </div>
              ) : (
                meals.map(meal => (
                  <div key={meal.idusersmeal} className="meal-card">
                    <div className="meal-header">
                      <span className="meal-type">{meal.meal_type}</span>
                      <span className="meal-day">{getDayLabel(meal.week_day)}</span>
                    </div>

                    <h4 className="meal-name">{meal.dish_name}</h4>

                    {meal.dish_description && (
                      <p className="meal-description">{meal.dish_description}</p>
                    )}

                    <div className="meal-info">
                      <div className="info-item">
                        <AccessTime sx={{ fontSize: 16 }} />
                        {formatTime(meal.meal_hour)}
                      </div>
                      <div className="info-item calories">
                        <LocalFireDepartment sx={{ fontSize: 16 }} />
                        {meal.total_calories?.toFixed(0) || 0} kcal
                      </div>
                    </div>

                    <div className="meal-actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDetail(meal)}
                        title="Ver detalhes"
                        aria-label="Ver detalhes"
                      >
                        <Visibility sx={{ fontSize: 16 }} />
                      </Button>
                      {canEdit() && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(meal)}
                            title="Editar"
                            aria-label="Editar"
                          >
                            <Edit sx={{ fontSize: 16 }} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteModal(meal)}
                            title="Excluir"
                            aria-label="Excluir"
                          >
                            <Delete sx={{ fontSize: 16 }} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </button>
                <span className="pagination-info">
                  Página {pagination.page} de {pagination.pages}
                </span>
                <button
                  className="pagination-btn"
                  disabled={pagination.page === pagination.pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <FormModal
        open={Boolean(selectedMeal && !showEditModal && !showDeleteModal)}
        onClose={closeDetail}
        title={selectedMeal?.dish_name || 'Detalhes da refeição'}
        large
      >
            {detailLoading ? (
              <div className="modal-loading">
                <Skeleton height={300} />
              </div>
            ) : mealDetail ? (
              <div className="modal-body">
                <div className="meal-detail-header">
                  <span className="meal-type-badge">{mealDetail.meal_type}</span>
                  <span className="meal-day-badge">{getDayLabel(mealDetail.week_day)}</span>
                  <span className="meal-time">
                    <AccessTime sx={{ fontSize: 16 }} />
                    {formatTime(mealDetail.meal_hour)}
                  </span>
                </div>

                {mealDetail.dish_description && (
                  <p className="meal-detail-description">{mealDetail.dish_description}</p>
                )}

                <div className="calories-display">
                  <LocalFireDepartment />
                  <span>{mealDetail.total_calories?.toFixed(0) || 0}</span>
                  <span className="unit">kcal</span>
                </div>

                {/* Ingredients */}
                <div className="ingredients-section">
                  <h4>Ingredientes ({mealDetail.ingredients?.length || 0})</h4>
                  {mealDetail.ingredients?.length > 0 ? (
                    <div className="ingredients-list">
                      {mealDetail.ingredients.map(ing => (
                        <div key={ing.idusersmealdetails} className="ingredient-item">
                          <div className="ingredient-info">
                            <span className="ingredient-name">{ing.name}</span>
                            <span className="ingredient-quantity">
                              {ing.quantity} {ing.unit}
                            </span>
                          </div>
                          <div className="ingredient-macros">
                            <span className="macro cal">{ing.calculated_calories?.toFixed(0)} kcal</span>
                            <span className="macro prot">{ing.calculated_protein?.toFixed(1)}g P</span>
                            <span className="macro carb">{ing.calculated_carbs?.toFixed(1)}g C</span>
                            <span className="macro fat">{ing.calculated_fat?.toFixed(1)}g G</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-ingredients">Nenhum ingrediente cadastrado</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="modal-error">
                <Warning sx={{ fontSize: 48 }} />
                <p>Erro ao carregar detalhes</p>
              </div>
            )}
      </FormModal>

      <FormModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Editar Refeição"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" form="meal-edit-form" loading={editLoading}>
              Salvar
            </Button>
          </>
        }
      >
            <form id="meal-edit-form" onSubmit={handleEdit}>
              <Input
                label="Nome do Prato"
                value={editForm.dish_name}
                onChange={(e) => setEditForm({ ...editForm, dish_name: e.target.value })}
                required
              />
              <TextArea
                label="Descrição"
                rows={3}
                value={editForm.dish_description}
                onChange={(e) => setEditForm({ ...editForm, dish_description: e.target.value })}
              />
            </form>
      </FormModal>

      <ConfirmModal
        open={showDeleteModal}
        title="Excluir Refeição"
        message={`Tem certeza que deseja excluir a refeição "${selectedMeal?.dish_name}"? Esta ação removerá também todos os ingredientes associados.`}
        confirmLabel="Excluir"
        loading={deleteLoading}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default UserMealsPage;

